import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Users, MapPin, User, Phone, Buildings, Lightning, ClockCounterClockwise, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format, addHours } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  num_personas: '',
  fecha_inicio: '',
  fecha_fin: '',
  direccion: '',
  direccion_llegada: '',
  referencia: '',
  responsable: '',
  telefono_contacto: '',
  area: '',
  materiales: '',
  motivo: '',
  justificacion: '',
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Algo salió mal. Por favor, inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

const fromExistingMov = (mov) => ({
  num_personas: mov.num_personas != null ? String(mov.num_personas) : '',
  fecha_inicio: toLocalInput(mov.fecha_inicio),
  fecha_fin: toLocalInput(mov.fecha_fin),
  direccion: mov.direccion || '',
  direccion_llegada: mov.direccion_llegada || '',
  referencia: mov.referencia || '',
  responsable: mov.responsable || '',
  telefono_contacto: mov.telefono_contacto || '',
  area: mov.area || '',
  materiales: mov.materiales || '',
  motivo: mov.motivo || '',
  justificacion: mov.justificacion || '',
});

export const NuevaMovilizacionDialog = ({ open, onOpenChange, onSuccess, existingMov = null }) => {
  const isEditMode = !!existingMov;
  const [loading, setLoading] = useState(false);
  const [tipo, setTipo] = useState('normal');
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    if (!open) return;
    if (isEditMode) {
      setFormData(fromExistingMov(existingMov));
      setTipo(existingMov.es_emergente ? 'emergente' : 'normal');
    } else {
      setFormData(INITIAL_FORM);
      setTipo('normal');
    }
  }, [open, isEditMode, existingMov]);

  const esEmergente = tipo === 'emergente';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (esEmergente && !formData.justificacion.trim()) {
      toast.error('La justificación es obligatoria para movilizaciones emergentes');
      return;
    }
    if (!esEmergente && !formData.motivo.trim()) {
      toast.error('El motivo es obligatorio para movilizaciones normales');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        num_personas: parseInt(formData.num_personas, 10),
        fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
        fecha_fin: new Date(formData.fecha_fin).toISOString(),
        materiales: formData.materiales.trim() || null,
        motivo: !esEmergente ? (formData.motivo.trim() || null) : null,
        es_emergente: esEmergente,
        justificacion: esEmergente ? formData.justificacion.trim() : null,
      };
      if (isEditMode) {
        await axios.patch(
          `${API_URL}/api/movilizaciones/${existingMov.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Movilización actualizada');
      } else {
        await axios.post(`${API_URL}/api/movilizaciones`, payload, { withCredentials: true });
        toast.success(
          esEmergente
            ? 'Movilización emergente creada. El equipo administrativo será notificado.'
            : 'Movilización creada exitosamente'
        );
      }
      onOpenChange(false);
      onSuccess();
      if (!isEditMode) {
        setFormData(INITIAL_FORM);
        setTipo('normal');
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  };

  const minDateTime = esEmergente
    ? format(new Date(), "yyyy-MM-dd'T'HH:mm")
    : format(addHours(new Date(), 48), "yyyy-MM-dd'T'HH:mm");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            {isEditMode ? 'Editar Movilización' : 'Nueva Movilización'}
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {isEditMode
              ? 'Modifica los datos de tu solicitud. Podrás editar mientras no haya sido aprobada o rechazada.'
              : 'Elige el tipo de agendamiento y completa los datos requeridos.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tipo} onValueChange={setTipo} className="mt-4">
          <TabsList data-testid="tipo-movilizacion-tabs" className="grid grid-cols-2 w-full bg-[#F1F3F5] p-1">
            <TabsTrigger
              value="normal"
              data-testid="tab-normal"
              className="gap-2 font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-white data-[state=active]:text-[#0A0A0A]"
            >
              <ClockCounterClockwise size={16} weight="bold" /> Normal (48h)
            </TabsTrigger>
            <TabsTrigger
              value="emergente"
              data-testid="tab-emergente"
              className="gap-2 font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#C1121F] data-[state=active]:text-white"
            >
              <Lightning size={16} weight="bold" /> Emergente
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {esEmergente && (
          <div className="mt-4 p-3 bg-[#C1121F]/10 border border-[#C1121F] flex items-start gap-3">
            <Warning size={20} weight="bold" className="text-[#C1121F] mt-0.5 flex-shrink-0" />
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">
              Modo <strong>Emergente</strong>: se omite la anticipación de 48 horas, pero es
              obligatorio justificar la solicitud.
            </p>
          </div>
        )}

        <form data-testid="nueva-movilizacion-form" onSubmit={handleSubmit} className="space-y-5 mt-4">
          {esEmergente && (
            <div className="space-y-2">
              <Label htmlFor="justificacion" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Justificación *
              </Label>
              <Textarea
                id="justificacion"
                name="justificacion"
                data-testid="justificacion-input"
                value={formData.justificacion}
                onChange={handleChange}
                placeholder="Describe el motivo emergente que impide agendar con 48 horas de anticipación..."
                className="border-[#C1121F] focus:border-[#8B0000] font-['IBM_Plex_Sans'] min-h-[100px]"
                required
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="num_personas" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Número de Personas *
              </Label>
              <div className="relative">
                <Users size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#495057]" weight="bold" />
                <Input
                  id="num_personas"
                  name="num_personas"
                  data-testid="num-personas-input"
                  type="number"
                  min="1"
                  value={formData.num_personas}
                  onChange={handleChange}
                  className="pl-10 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="area" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Área Solicitante *
              </Label>
              <div className="relative">
                <Buildings size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#495057]" weight="bold" />
                <Input
                  id="area"
                  name="area"
                  data-testid="area-input"
                  type="text"
                  value={formData.area}
                  onChange={handleChange}
                  className="pl-10 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_inicio" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Fecha y Hora de Salida *
              </Label>
              <Input
                id="fecha_inicio"
                name="fecha_inicio"
                data-testid="fecha-inicio-input"
                type="datetime-local"
                min={minDateTime}
                value={formData.fecha_inicio}
                onChange={handleChange}
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                required
              />
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">
                {esEmergente ? 'Sin restricción de anticipación mínima' : 'Mínimo 48 horas de anticipación'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha_fin" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Fecha y Hora de Finalización *
              </Label>
              <Input
                id="fecha_fin"
                name="fecha_fin"
                data-testid="fecha-fin-input"
                type="datetime-local"
                min={formData.fecha_inicio || minDateTime}
                value={formData.fecha_fin}
                onChange={handleChange}
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="direccion" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Sitio de Salida *
            </Label>
            <div className="relative">
              <MapPin size={18} className="absolute left-3 top-3 text-[#495057]" weight="bold" />
              <Textarea
                id="direccion"
                name="direccion"
                data-testid="direccion-input"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Ej: Sede provincial CRE JPP, Av. Colombia N4-236"
                className="pl-10 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] min-h-[80px]"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="direccion_llegada" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Lugar de Llegada *
            </Label>
            <div className="relative">
              <MapPin size={18} className="absolute left-3 top-3 text-[#495057]" weight="bold" />
              <Textarea
                id="direccion_llegada"
                name="direccion_llegada"
                data-testid="direccion-llegada-input"
                value={formData.direccion_llegada}
                onChange={handleChange}
                placeholder="Ej: Escuela Fiscal Manuela Cañizares, Av. 6 de Diciembre y Colón"
                className="pl-10 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] min-h-[80px]"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="referencia" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Referencia *
            </Label>
            <Textarea
              id="referencia"
              name="referencia"
              data-testid="referencia-input"
              value={formData.referencia}
              onChange={handleChange}
              placeholder="Ej: Edificio color azul, frente al parque..."
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] min-h-[80px]"
              required
            />
          </div>

          {!esEmergente && (
            <div className="space-y-2">
              <Label htmlFor="motivo" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Motivo de la Movilización *
              </Label>
              <Textarea
                id="motivo"
                name="motivo"
                data-testid="motivo-input"
                value={formData.motivo}
                onChange={handleChange}
                placeholder="Describe el motivo institucional por el que se necesita esta movilización..."
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] min-h-[90px]"
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="materiales" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Materiales a Transportar <span className="text-[#6C757D] font-normal">(opcional)</span>
            </Label>
            <Textarea
              id="materiales"
              name="materiales"
              data-testid="materiales-input"
              value={formData.materiales}
              onChange={handleChange}
              placeholder="Ej: 5 cajas con insumos médicos, 2 tanques de oxígeno..."
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="responsable" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Responsable de la Actividad *
              </Label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#495057]" weight="bold" />
                <Input
                  id="responsable"
                  name="responsable"
                  data-testid="responsable-input"
                  type="text"
                  value={formData.responsable}
                  onChange={handleChange}
                  className="pl-10 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefono_contacto" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Teléfono de Contacto *
              </Label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#495057]" weight="bold" />
                <Input
                  id="telefono_contacto"
                  name="telefono_contacto"
                  data-testid="telefono-contacto-input"
                  type="tel"
                  value={formData.telefono_contacto}
                  onChange={handleChange}
                  className="pl-10 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              data-testid="submit-movilizacion-button"
              type="submit"
              disabled={loading}
              className={`flex-1 text-white font-['IBM_Plex_Sans'] transition-colors ${
                esEmergente
                  ? 'bg-[#C1121F] hover:bg-[#8B0000]'
                  : 'bg-[#0A0A0A] hover:bg-[#333333]'
              }`}
            >
              {loading
                ? (isEditMode ? 'Guardando...' : 'Creando...')
                : isEditMode
                ? 'Guardar Cambios'
                : esEmergente
                ? 'Crear Movilización Emergente'
                : 'Crear Movilización'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
